import type { CSSProperties, RefObject } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

export interface UseFitScaleOptions {
  /** Floor the scale never goes below (e.g. the audit's worst case still reads at this size). Default 0.78. */
  min?: number;
}

/**
 * Fit-to-box, not truncation (media-pair fix round 3): when the element
 * `ref` points at needs more height than the fixed-height box it renders
 * inside (its `scrollHeight` exceeds its `clientHeight` — the box itself is
 * sized by an ancestor, e.g. a CSS-grid row stretched to a sibling's
 * height), this scales the element DOWN uniformly (so text/chips/icons
 * shrink together, nothing distorts) until it fits, floored at `min` so
 * even the worst-case content (max chips, 2-line role, max-length callout)
 * stays legible. Reference-size content that already fits (typical decks)
 * gets scale 1 — a no-op.
 *
 * The element itself must have a bounded (non-auto) height — e.g.
 * `height: 100%` inside a fixed-height parent — for `clientHeight` to stay
 * at the AVAILABLE space rather than growing to match content; pair this
 * hook's returned scale with `fitScaleStyle` below, which also sets
 * `width: calc(100% / scale)` so the visual width after the transform
 * still matches the box (the transform alone would shrink width too).
 *
 * Nothing is ever clipped and nothing is ever ellipsized here — every
 * character the agent authored still renders, just smaller.
 *
 * SSR/print-safe: a no-op (`typeof window === 'undefined'`) when there is
 * no DOM. Runs in a layout effect — synchronous, before paint — so a
 * captured print/export view already reflects the final scale rather than
 * flashing overflowing content first.
 */
/**
 * Read a single element's own natural fit scale — the guts shared by
 * `useFitScale` and `useSharedFitScale`.
 *
 * Fix round 7: scale can't be computed from a single measurement at the
 * natural (unscaled) width. `fitScaleStyle` doesn't just `transform:
 * scale(s)` — it ALSO widens the box to `calc(100% / s)` so the transform's
 * shrink lands back on 100% visually. At that wider width the same content
 * wraps into FEWER lines, so its `scrollHeight` at the width it will
 * actually render at is shorter than `scrollHeight` at the natural
 * (narrower) width. A one-shot `s = available / naturalHeight` computes s
 * from the WRONG (taller) height, which always under-scales — the box ends
 * up bigger than it needs to be, i.e. scaled down more than necessary, with
 * empty space below the content.
 *
 * The fix: binary-search for the largest `s` in `[min, 1]` such that the
 * content, laid out at the WIDTH IT WOULD ACTUALLY RENDER AT for that s
 * (`width: 100% / s`, same as `fitScaleStyle`), fits once shrunk
 * (`scrollHeight(s) * s <= available`). Each probe writes the same
 * transform/width pair `fitScaleStyle` would render, forces layout by
 * reading `scrollHeight`, and moves the search bound accordingly — so the
 * measurement always matches what will actually be on screen, and there is
 * no separate "reset to natural width, read, restore" step to fight with
 * that (the old oscillation this guarded against — measuring at natural
 * width, concluding "fits", resetting to 100%, re-wrapping taller, shrinking
 * again next tick — can't happen because the search only ever measures at
 * the width a given `s` would actually use).
 *
 * `s = 1` (width 100%, no transform — `fitScaleStyle`'s no-op case) is
 * checked first and returned directly when it already fits, since that's
 * the common case (typical decks) and skips the search entirely.
 *
 * All probe styles are restored to whatever this render's JSX last set
 * before returning, so this is a read-then-restore against the live node,
 * not a competing write — React's own control of the `style` prop on the
 * next render is unaffected.
 *
 * Returns `null` when the node hasn't been laid out yet (zero height either
 * way) — callers should skip it rather than let a stray 0 win a `Math.min`.
 */
function measureNaturalScale(node: HTMLElement, min: number): number | null {
  const prevTransform = node.style.transform;
  const prevWidth = node.style.width;

  const probe = (s: number): number => {
    if (s >= 1) {
      node.style.transform = 'none';
      node.style.width = '100%';
    } else {
      node.style.transform = `scale(${s})`;
      node.style.width = `${100 / s}%`;
    }
    // Reading `scrollHeight` forces the browser to flush the style change
    // above into layout before returning a value — this IS the "force
    // layout" step, not just a read.
    return node.scrollHeight;
  };

  const restore = () => {
    node.style.transform = prevTransform;
    node.style.width = prevWidth;
  };

  // Measure `clientHeight` (the available space) right after the `s = 1`
  // probe, i.e. with the SAME width/transform in effect that `scrollHeight`
  // above was just read against — matching how the two were always read
  // together before this fix, in case `clientHeight` is sensitive to the
  // node's own width in some layout (e.g. a box-sizing/scrollbar edge case)
  // even though the hook's contract assumes a height bounded by an ancestor.
  const naturalHeight = probe(1);
  const availableHeight = node.clientHeight;
  if (naturalHeight <= 0 || availableHeight <= 0) {
    restore();
    return null;
  }
  if (naturalHeight <= availableHeight + 0.5) {
    restore();
    return 1;
  }

  const fits = (s: number): boolean => probe(s) * s <= availableHeight + 0.5;

  let best = min;
  if (fits(min)) {
    best = min;
    let lo = min;
    let hi = 1;
    // ~7 iterations of bisection narrows [min, 1] (span <= 0.22 in the
    // hook's 0.78 default floor) to well under a visually meaningful
    // scale difference.
    for (let i = 0; i < 7; i += 1) {
      const mid = (lo + hi) / 2;
      if (fits(mid)) {
        best = mid;
        lo = mid;
      } else {
        hi = mid;
      }
    }
  }
  // else: doesn't fit even at the floor — `best` stays `min`, the largest
  // scale-down this hook is allowed to apply; the caller renders it anyway
  // (nothing is ever clipped or ellipsized, only shrunk, per this hook's
  // contract) rather than shrinking further.

  restore();
  return best;
}

/**
 * Fix round 6: the `requestAnimationFrame`-deferred re-measurements above
 * catch layout settling that happens within a couple of frames (a grid row
 * finalizing, a sibling column's height propagating), but a vendored web
 * font (`Inter`, `font-display: swap` — see `styles.css`) is a network/disk
 * fetch that routinely takes longer than that: the very first measurement(s)
 * run against the FALLBACK font's metrics, which wrap text differently (in
 * either direction) than Inter's. Once Inter swaps in, the element's own
 * border box doesn't change size (it's pinned to `height: 100%` of a fixed
 * grid/flex ancestor), so the `ResizeObserver` this hook also installs never
 * fires to trigger a re-measurement — the scale computed from the fallback
 * font's (wrong) natural height sticks forever, even though the box's TRUE
 * natural height (Inter's) is now different. `document.fonts.ready` resolves
 * exactly when every `@font-face` the page requested has either loaded or
 * failed, so a re-measure keyed off it always reflects the font that will
 * actually render from then on. `window`'s `load` event is a second,
 * independent catch-all for the same class of "content changed size without
 * the box itself resizing" case (e.g. an image inside the measured element
 * finishing decode) that fonts.ready wouldn't cover.
 *
 * Both listeners call `measure` (not `measureNaturalScale` directly) so a
 * font that finishes loading after the component unmounted is a no-op (the
 * closure's own `ref.current` / mounted checks are `measure`'s job), and
 * both are registered once per mount alongside the `ResizeObserver` — same
 * lifetime, same cleanup.
 */
function scheduleFontAndLoadRemeasure(measure: () => void): () => void {
  let cancelled = false;
  const guarded = () => {
    if (!cancelled) measure();
  };
  const fontSet = typeof document !== 'undefined' ? (document as Document & { fonts?: FontFaceSet }).fonts : undefined;
  fontSet?.ready.then(guarded);
  if (typeof window !== 'undefined') {
    window.addEventListener('load', guarded);
  }
  return () => {
    cancelled = true;
    if (typeof window !== 'undefined') {
      window.removeEventListener('load', guarded);
    }
  };
}

export function useFitScale(ref: RefObject<HTMLElement | null>, options?: UseFitScaleOptions): number {
  const min = options?.min ?? 0.78;
  const [scale, setScale] = useState(1);
  // Re-run measurement on the latest `min` without re-subscribing the
  // ResizeObserver every render (it only needs to be created once per ref).
  const minRef = useRef(min);
  minRef.current = min;

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const el = ref.current;
    if (!el) return undefined;

    const measure = () => {
      const node = ref.current;
      if (!node) return;
      const next = measureNaturalScale(node, minRef.current);
      if (next === null) return;
      setScale((prev) => (Math.abs(prev - next) < 0.005 ? prev : next));
    };

    measure();
    // A `ResizeObserver` only fires again once the element's OWN border box
    // changes size (e.g. the `width` this hook itself sets on a rescale).
    // It does NOT fire just because a CHILD's content re-wrapped without
    // moving the (fixed-height) box's own edges — e.g. a web font finishing
    // its swap a frame or two after this first synchronous measurement, or
    // the grid row this box is stretched to being finalized a tick later
    // than the initial commit. A couple of `requestAnimationFrame`-deferred
    // re-measurements after mount catch that settling without needing a
    // continuous poll.
    const raf1 = requestAnimationFrame(() => {
      measure();
      requestAnimationFrame(measure);
    });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const cancelFontAndLoadRemeasure = scheduleFontAndLoadRemeasure(measure);
    return () => {
      cancelAnimationFrame(raf1);
      ro.disconnect();
      cancelFontAndLoadRemeasure();
    };
  }, [ref]);

  return scale;
}

/**
 * Same fit-to-box mechanism as `useFitScale`, but for a PAIR of elements
 * that must always share ONE scale (media-pair-contract.md "Slide 10" —
 * the two `dual_path` path cards). Each card has its own independent
 * natural fit scale; the SMALLER of the two wins for BOTH, so side-by-side
 * type always matches instead of one card looking visibly smaller than the
 * other whenever their content happens to differ in length. The rail
 * callout is unaffected — it keeps its own independent `useFitScale`.
 *
 * Runs the same per-node binary search as the single-element hook
 * (`measureNaturalScale`) independently on each card, then takes the
 * `Math.min` of the two results. A card whose own search lands on a scale
 * smaller than its sibling's still fits when forced to the sibling's
 * (larger) scale rendered at that sibling's narrower compensating width —
 * a wider-than-its-own-probe width only makes content wrap LESS, never
 * more — so sharing the smaller of the two is always safe for both. A node
 * that hasn't laid out yet (or is absent, e.g. only one path exists) is
 * skipped rather than dragging the shared scale to 0.
 */
export function useSharedFitScale(
  refA: RefObject<HTMLElement | null>,
  refB: RefObject<HTMLElement | null>,
  options?: UseFitScaleOptions,
): number {
  const min = options?.min ?? 0.78;
  const [scale, setScale] = useState(1);
  const minRef = useRef(min);
  minRef.current = min;

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const refs = [refA, refB];
    const elements = refs.map((r) => r.current).filter((node): node is HTMLElement => !!node);
    if (elements.length === 0) return undefined;

    const measure = () => {
      let shared: number | null = null;
      for (const ref of refs) {
        const node = ref.current;
        if (!node) continue;
        const next = measureNaturalScale(node, minRef.current);
        if (next === null) continue;
        shared = shared === null ? next : Math.min(shared, next);
      }
      if (shared === null) return;
      setScale((prev) => (Math.abs(prev - shared!) < 0.005 ? prev : shared!));
    };

    measure();
    const raf1 = requestAnimationFrame(() => {
      measure();
      requestAnimationFrame(measure);
    });
    const ro = new ResizeObserver(measure);
    elements.forEach((el) => ro.observe(el));
    const cancelFontAndLoadRemeasure = scheduleFontAndLoadRemeasure(measure);
    return () => {
      cancelAnimationFrame(raf1);
      ro.disconnect();
      cancelFontAndLoadRemeasure();
    };
  }, [refA, refB]);

  return scale;
}

/** `style` prop for the element `useFitScale` measured — see that function's doc for why `width` compensates. */
export function fitScaleStyle(scale: number): CSSProperties {
  if (scale >= 1) {
    return { transform: 'none', width: '100%' };
  }
  return {
    transform: `scale(${scale})`,
    transformOrigin: 'top left',
    width: `calc(100% / ${scale})`,
  };
}
